import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Megaphone,
  ListChecks,
  CheckCircle,
  FileText,
  LogOut,
  ChevronLeft,
  Users,
  History,
  ShieldCheck,
  Calculator
} from 'lucide-react';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/campaigns', label: 'Kampanyalar', icon: Megaphone },
  { path: '/admin/tasks', label: 'Görevler', icon: ListChecks },
  { path: '/admin/pool-model', label: 'Havuz Modeli', icon: Calculator },
  { path: '/admin/approvals', label: 'Onay Ekranı', icon: CheckCircle },
  { path: '/admin/users', label: 'Kullanıcılar', icon: Users },
  { path: '/admin/content', label: 'İçerik Yönetimi', icon: FileText },
  { path: '/admin/admin-management', label: 'Admin Yönetimi', icon: ShieldCheck },
  { path: '/admin/audit-logs', label: 'Giriş Geçmişi', icon: History },
];

export default function AdminLayout() {
  const { signOut, user, profile } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-brand-beige flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-brand-line flex flex-col">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-brand-line">
          <img src="/brand/appstars-logo.png" alt="Appstars" className="h-8 w-auto" data-testid="admin-logo" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`admin-nav-${item.path.split('/').pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-neon text-brand-ink font-semibold ring-1 ring-brand-ink'
                    : 'text-gray-700 hover:bg-brand-beige'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-brand-line">
          <div className="mb-3 px-3">
            <p className="text-xs text-gray-500">Giriş yapan:</p>
            <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="h-16 bg-brand-beige border-b border-brand-line flex items-center px-6">
          <Button
            variant="ghost"
            size="sm"
            className="mr-4"
            onClick={() => navigate('/')}
            data-testid="admin-back-home"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Siteye Dön
          </Button>
          <h1 className="font-brand text-lg text-brand-ink" data-testid="admin-header-title">
            Admin Panel
          </h1>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
